import { createFileRoute } from "@tanstack/react-router";
import type { ClientStudioDocMetadataForBuilder } from "api-types/builders/buildersResponses";
import { filterListBySearchAndFilters } from "common/utils/search";
import BuilderPortalEmptyCTA from "lib/components/builders/BuilderPortalEmptyCTA";
import BuilderPortalLayout from "lib/components/builders/BuilderPortalLayout";
import SectionHeader from "lib/components/builders/communities/plans/SectionHeader";
import StudioDocEventLog from "lib/components/builders/files/StudioDocEventLog";
import { filterLabels, filterSets } from "lib/components/builders/files/StudioFileConstants";
import StudioFilesTable from "lib/components/builders/files/StudioFilesTable";
import getStudioFileStatus from "lib/components/builders/files/StudioFileUtils";
import FilterDrawer from "lib/components/builders/search/FilterDrawer";
import SearchAndFilterControls from "lib/components/builders/search/SearchAndFilterControls";
import { getBuilderStudioDocsURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { studioPath, useAppRouterParams } from "lib/routeHelpers";
import {
  clearFilters,
  toggleShowUntitled,
  updateFilter,
  updateSearchText,
} from "lib/state/studioDocBrowserFiltersActions";
import studioDocBrowserFiltersReducer, {
  initialStudioDocBrowserFiltersState,
} from "lib/state/studioDocBrowserFiltersReducer";
import { useUserContext } from "lib/state/UserContextProvider";
import getUserFullName from "lib/utils/userName";
import { useEffect, useReducer, useState } from "react";
import Button from "ui/buttons/Button";
import NewEntityButton from "ui/buttons/NewEntityButton/NewEntityButton";
import Loading from "ui/components/Loading";
import cx from "ui/cx";
import useTabbedMenu from "ui/hooks/useTabbedMenu";
import EmptySearchResults from "ui/search/EmptySearchResults";
import TextWithTooltipIcon from "ui/text/TextWithTooltipIcon";
import withAuthentication from "../../../components/withAuthentication";
import styles from "./files.module.scss";

type StudioFilesPageTab = "files" | "history";

function StudioFilesPage() {
  const { builderId } = useAppRouterParams();

  if (!builderId) {
    return null;
  }

  return <ReadyStudioFilesPage builderId={builderId} />;
}

function ReadyStudioFilesPage({ builderId }: { builderId: string }) {
  const { tab, menuItems } = useTabbedMenu<StudioFilesPageTab>(["files", "history"], {
    prettyNames: { files: "Files", history: "History" },
  });

  const [filterTrayOpen, setFilterTrayOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [{ searchText, filters, showUntitled, totalFilterCount }, dispatch] = useReducer(
    studioDocBrowserFiltersReducer,
    initialStudioDocBrowserFiltersState,
  );

  const { hasPermission, user } = useUserContext();

  const {
    data: studioFiles,
    loading,
    mutate: mutateStudioFiles,
  } = useFetch<ClientStudioDocMetadataForBuilder[]>(getBuilderStudioDocsURL(builderId, { showUntitled }), [builderId]);

  const [filteredFiles, setFilteredFiles] = useState<ClientStudioDocMetadataForBuilder[]>([]);

  // Update the set of filtered studio files displayed in the table whenever:
  // 1. the set of studio files from the API call changes, or:
  // 2. the user changes the search text, or:
  // 3. the user changes any of the filters.
  useEffect(() => {
    const updatedFilteredFiles = filterListBySearchAndFilters(
      studioFiles ?? [],
      searchText,
      filters,
      getFileSearchableValues,
      getFileFilterValue,
    );
    setFilteredFiles(updatedFilteredFiles);
  }, [searchText, filters, studioFiles]);

  // Returns the set of values in a file that can be used to match the search text.
  // The file matches the search text if any of these values contain the search text (case insensitive).
  const getFileSearchableValues = (file: ClientStudioDocMetadataForBuilder) => {
    const owner = file.users.find((user) => user.userRole === "owner");
    const ownerName = getUserFullName(owner ?? null);
    return [file.studioDoc?.name, ownerName];
  };

  // Returns the file value that corresponds to a given filter name.
  // This value will be used to determine whether the file matches the filter.
  const getFileFilterValue = (file: ClientStudioDocMetadataForBuilder, filterName: string) => {
    switch (filterName) {
      case "yourRole": {
        const currUser = file.users.find(({ id }) => id === user?.id);
        // If the current user has no explicit association with the file, their
        // implicit Viewer role should be treated the same way as an explicit Viewer
        // role for filtering purposes.
        if (!currUser) {
          return "viewer";
        }
        return currUser.userRole;
      }
      case "status": {
        return getStudioFileStatus(file.studioDoc);
      }
    }
    return undefined;
  };

  const onDeleteFile = (studioDocId: string) => {
    const updatedStudioFiles = studioFiles?.filter((file) => file.studioDoc.id !== studioDocId);
    void mutateStudioFiles(updatedStudioFiles);
  };

  const newStudioFileButton = (
    <NewEntityButton
      entityName="Studio File"
      linkUrl={studioPath(undefined, builderId)}
      disabled={!hasPermission("EDIT_STUDIO_FILES")}
      disabledTooltipText="Only users with the Admin, Sales Manager or Drafter role can create a new Studio File."
    />
  );

  const noStudioFiles = !studioFiles?.length;
  const hasStudioFiles = !loading && !noStudioFiles;
  const emptySearchResults = hasStudioFiles && (totalFilterCount > 0 || searchText) && !filteredFiles.length;

  return (
    <>
      <BuilderPortalLayout
        header="Studio Files"
        headerActions={newStudioFileButton}
        selected="studioFiles"
        pageTitle="Studio Files"
        menuItems={menuItems}
      >
        {tab === "files" && (
          <div className={`builder-portal-layout-content ${noStudioFiles ? "spacing-top" : ""}`}>
            {!loading && noStudioFiles && (
              <BuilderPortalEmptyCTA>
                <div className="cta-container">
                  <div className="cta-title">No Studio Files</div>
                  <div className="cta-text">
                    You can create a new Studio File from here or from the Profile menu in the top right corner of this
                    page, click Studio.
                  </div>
                  {newStudioFileButton}
                </div>
              </BuilderPortalEmptyCTA>
            )}
            {hasStudioFiles && (
              <SectionHeader
                title={<TextWithTooltipIcon text="Studio Files" tooltip="Create and manage your Studio files here." />}
                headerSearch={
                  <SearchAndFilterControls
                    id="filesSearch"
                    searchText={searchText}
                    totalFilterCount={totalFilterCount}
                    onSearchTextChange={(text: string) => dispatch(updateSearchText(text))}
                    onClearFilters={() => dispatch(clearFilters())}
                    onIsOpenChange={(open: boolean) => setFilterTrayOpen(open)}
                    onShowFiltersChange={(open: boolean) => setShowFilters(open)}
                  />
                }
                hr
              />
            )}
            {loading && <Loading />}
            {hasStudioFiles && (
              <FilterDrawer
                open={filterTrayOpen}
                showFilters={showFilters}
                filterSets={filterSets}
                filterLabels={filterLabels}
                filters={filters}
                onUpdateFilter={(name: string, values: string[]) => dispatch(updateFilter({ name, values }))}
                searchText={searchText}
                filteredResultCount={filteredFiles.length}
                additionalFilters={
                  <Button
                    btnSize="medium"
                    btnStyle="secondary"
                    className={cx(styles.toggleButton, showUntitled && styles.selected)}
                    onClick={() => dispatch(toggleShowUntitled())}
                  >
                    Show Untitled
                  </Button>
                }
              />
            )}
            {hasStudioFiles && !emptySearchResults && (
              <StudioFilesTable files={filteredFiles} onDeleteFile={onDeleteFile} />
            )}
            {emptySearchResults && <EmptySearchResults />}
          </div>
        )}
        {tab === "history" && (
          <div className="builder-portal-layout-content">
            <StudioDocEventLog builderId={builderId} />
          </div>
        )}
      </BuilderPortalLayout>
    </>
  );
}

export const Route = createFileRoute("/builders/$builder_id/files")({
  component: withAuthentication(StudioFilesPage, {
    permissions: ["VIEW_STUDIO_FILES"],
    products: ["studio"],
  }),
});
